import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';

import avatar from 'assets/images/avatar.png';
import { status } from 'constants/general';
import profilesStrings from 'modules/profiles/strings';
import SingleFileUploader from 'modules/common/components/SingleFileUploader';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  LoaderWrapper,
  UpdateProfileWrapper,
  NoResultsWrapper,
  NoResultsText,
  FormWrapper,
  FormTitle,
  FormGrid,
  FormFieldWrapper,
  FormLabel,
  FormTextInput,
  FormTextAreaInput,
  FormImage,
  FormValidationError,
  FormBtnWrapper,
  FormBtn,
} from './UpdateProfileComponent.style';

const UpdateProfileComponent = ({
  formFieldsValues,
  shouldShowErrorMessage,
  handleTextInputFieldChange,
  handleUploadFileChange,
  handleUpdateProfileBtnClick,
}) => {
  const { companyProfilesList, profileDetails, updateProfileInfo } = useSelector(state => ({
    companyProfilesList: state.common.companyProfilesList,
    profileDetails: state.profiles.profileDetails,
    updateProfileInfo: state.profiles.updateProfileInfo,
  }));

  const profileImage = profileDetails?.details?.imageUrl || avatar;

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  if (
    companyProfilesList.status === status.SHOULD_CALL_API ||
    companyProfilesList.status === status.FETCHING ||
    profileDetails.status === status.SHOULD_CALL_API ||
    profileDetails.status === status.FETCHING
  )
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );

  if (profileDetails.status === status.FAIL)
    return (
      <NoResultsWrapper>
        <NoResultsText>{profilesStrings.updateProfileForm.noSelectedCompany}</NoResultsText>
      </NoResultsWrapper>
    );

  return (
    <UpdateProfileWrapper>
      <FormWrapper>
        <FormTitle>{profilesStrings.updateProfileForm.updateProfileFormTitle}</FormTitle>
        <SectionDivider gap={16} withSeparator separatorGap={16} borderColor={colors.grey004} />
        <FormGrid>
          {/* Image Field */}
          <FormFieldWrapper fullWidth center>
            <FormImage src={profileImage} />
          </FormFieldWrapper>

          {/* Name Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>
              {profilesStrings.updateProfileForm.updateProfileFormNameFieldPlaceholder}
            </FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.name}
              maxLength="32"
              onChange={event => handleTextInputFieldChange('name', event)}
            />
          </FormFieldWrapper>

          {/* Description Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>
              {profilesStrings.updateProfileForm.updateProfileFormDescriptionFieldPlaceholder}
            </FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.description}
              maxLength="100"
              onChange={event => handleTextInputFieldChange('description', event)}
            />
          </FormFieldWrapper>

          {/* Long Description Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>
              {profilesStrings.updateProfileForm.updateProfileFormLongDescriptionFieldPlaceholder}
            </FormLabel>
            <SectionDivider gap={8} />
            <FormTextAreaInput
              type="text"
              value={formFieldsValues.longDescription}
              maxLength="1500"
              onChange={event => handleTextInputFieldChange('longDescription', event)}
            />
          </FormFieldWrapper>

          {/* Email Field */}
          <FormFieldWrapper>
            <FormLabel>
              {profilesStrings.updateProfileForm.updateProfileFormEmailFieldPlaceholder}
            </FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.email}
              onChange={event => handleTextInputFieldChange('email', event)}
            />
          </FormFieldWrapper>

          {/* Website Field */}
          <FormFieldWrapper>
            <FormLabel>
              {profilesStrings.updateProfileForm.updateProfileFormWebsiteFieldPlaceholder}
            </FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.url}
              onChange={event => handleTextInputFieldChange('url', event)}
            />
          </FormFieldWrapper>

          {/* Update Image Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>
              {profilesStrings.updateProfileForm.updateProfileFormImageFieldPlaceholder}
            </FormLabel>
            <SectionDivider gap={8} />
            <SingleFileUploader
              fileData={formFieldsValues.imageData}
              setUploadedData={handleUploadFileChange}
            />
          </FormFieldWrapper>
        </FormGrid>
        {shouldShowErrorMessage === true && (
          <>
            <SectionDivider gap={15} />
            <FormValidationError centerText>
              {profilesStrings.updateProfileForm.requestErrorMessage}
            </FormValidationError>
          </>
        )}
        <SectionDivider gap={35} />
        <FormBtnWrapper>
          <FormBtn as="button" onClick={handleUpdateProfileBtnClick}>
            {(updateProfileInfo.status === status.FETCHING && (
              <Loader width={14} height={14} thickness={4} />
            )) ||
              profilesStrings.updateProfileForm.updateProfileFormSaveBtn}
          </FormBtn>
        </FormBtnWrapper>
      </FormWrapper>
    </UpdateProfileWrapper>
  );
};

UpdateProfileComponent.propTypes = {
  formFieldsValues: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    longDescription: PropTypes.string,
    email: PropTypes.string,
    url: PropTypes.string,
    imageData: PropTypes.shape({}),
  }).isRequired,
  shouldShowErrorMessage: PropTypes.bool.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleUploadFileChange: PropTypes.func.isRequired,
  handleUpdateProfileBtnClick: PropTypes.func.isRequired,
};

export default UpdateProfileComponent;
